import { useMsal, useIsAuthenticated, AuthenticatedTemplate } from "@azure/msal-react";
import { InteractionStatus, InteractionRequiredAuthError } from "@azure/msal-browser";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { loginRequest, graphRequest } from '../security/authConfig';
import SessionStorageService from "../services/SessionStorageService";
import { getConfiguredMsalAuthority } from "../security/msalInstance";
import { isMsalInteractionInProgress } from "../security/AzureAuthService";
import msalInstance from "../security/msalInstance";

const getAuthErrorFromUrl = () => {
  try {
    const search = new URLSearchParams(window.location.search || "");
    const hash = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));

    const error =
      search.get("error") || hash.get("error") || search.get("errorCode") || null;
    const errorDescription =
      search.get("error_description") ||
      hash.get("error_description") ||
      search.get("errorDescription") ||
      null;

    if (!error && !errorDescription) return null;
    return [error, errorDescription].filter(Boolean).join(": ");
  } catch {
    return null;
  }
};

const hasAuthResponseInUrl = () => {
  const href = window.location.href || "";
  const hash = window.location.hash || "";
  const search = window.location.search || "";
  return (
    href.includes("code=") ||
    href.includes("error=") ||
    hash.includes("code=") ||
    hash.includes("error=") ||
    search.includes("code=") ||
    search.includes("error=")
  );
};

const clearAuthResponseFromHash = () => {
  const hash = window.location.hash || "";
  if (!hash) return;
  // MSAL auth response comes back as "#code=..." or "#error=...".
  if (hash.startsWith("#code=") || hash.startsWith("#error=")) {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
  }
};

const isInteractionInProgressError = (error) => {
  const errorCode = error?.errorCode || "";
  const errorMessage = error?.errorMessage || error?.message || "";
  return errorCode === "interaction_in_progress" || errorMessage.includes("interaction_in_progress");
};

const normalizeScopes = (scopes) => {
  if (!scopes) return null;
  if (Array.isArray(scopes)) {
    const list = scopes.map((scope) => String(scope).trim()).filter(Boolean);
    return list.length ? list : null;
  }
  if (typeof scopes === "string") {
    const list = scopes.split(/\s+/).map((scope) => scope.trim()).filter(Boolean);
    return list.length ? list : null;
  }
  return null;
};

const getInitials = (name) => {
  if (!name) return "";
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "";
  const initials = parts.slice(0, 2).map((part) => part[0].toUpperCase()).join("");
  return initials || "";
};

const SIGN_IN_LOCK_KEY = "formBuilder.signInLocked";

const hasLoginLock = () => {
  try {
    return localStorage.getItem(SIGN_IN_LOCK_KEY) === "true";
  } catch {
    return false;
  }
};

const setLoginLock = () => {
  try {
    localStorage.setItem(SIGN_IN_LOCK_KEY, "true");
  } catch {
    // ignore storage failures
  }
};

const ProfileContent = ({ isEng, displayName, photoUrl, showProfilePicture }) => {
  const greeting = isEng ? "Welcome" : "Bienvenue";
  const initials = getInitials(displayName);
  const altText = isEng
    ? `Profile photo for ${displayName || "user"}`
    : `Photo de profil de ${displayName || "utilisateur"}`;

  return (
    <div className="white" style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      <span>{displayName || ""}</span>
      {showProfilePicture && (
        photoUrl ? (
          <img
            src={photoUrl}
            alt={altText}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #fff",
            }}
          />
        ) : (
          <span
            aria-hidden="true"
            title={displayName || ""}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "#fff",
              color: "#0369AC",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 700,
              border: "2px solid #fff",
            }}
          >
            {initials || "?"}
          </span>
        )
      )}
    </div>
  );
};

export default function UserComponent({
  callback,
  categoryId,
  scopes,
  isEng,
  showProfilePicture = false,
  lockAfterFirstLogin = false,
}) {
  const { instance, inProgress, accounts } = useMsal();
  const [accessDenied, setAccessDenied] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginLocked, setLoginLocked] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const isAuthenticated = useIsAuthenticated();
  const redirectHandledRef = useRef(false);
  const photoUrlRef = useRef(null);
  const photoAccountRef = useRef(null);
  const fetchingPhotoRef = useRef(false);
  const baseAuthRequest = loginRequest || { scopes: [] };
  const resolvedScopes = useMemo(
    () => normalizeScopes(scopes) ?? baseAuthRequest.scopes,
    [scopes, baseAuthRequest],
  );
  const authority = getConfiguredMsalAuthority();
  const authRequest = useMemo(
    () => ({ ...baseAuthRequest, scopes: resolvedScopes, ...(authority ? { authority } : {}) }),
    [baseAuthRequest, resolvedScopes, authority],
  );

  const beginInteractiveLogin = async (request, usePopup = false) => {
    if (isMsalInteractionInProgress()) {
      return null;
    }

    try {
      if (usePopup) {
        return await instance.loginPopup(request);
      }

      await instance.loginRedirect(request);
      return null;
    } catch (error) {
      if (isInteractionInProgressError(error)) {
        return null;
      }
      throw error;
    }
  };

  // ✅ Utility function to acquire token
  const acquireToken = async (scopes = authRequest.scopes, usePopup = false) => {
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) {
      console.warn("No account available for token acquisition.");
      return;
    }

    try {
      const response = await instance.acquireTokenSilent({ scopes, account, ...(authority ? { authority } : {}) });
      console.log("Token acquired for scopes:", scopes);
      setLoginError(null);
      return response.accessToken;
    } catch (error) {
      console.error("Silent token acquisition failed:", error);
      setLoginError(error?.errorMessage || error?.message || String(error));
       if (
          error.errorCode === "AADSTS50105" ||
          (error.message && error.message.includes("AADSTS50105"))
        ) {
          setAccessDenied(true);
        } 
      if (
        error instanceof InteractionRequiredAuthError ||
        error.name === "InteractionRequiredAuthError" ||
        error.errorCode === "interaction_required"
      ) {
        try {
          const interactiveRequest = { scopes, ...(authority ? { authority } : {}) };
          const response = await beginInteractiveLogin(interactiveRequest, usePopup);
          if (response?.account) {
            instance.setActiveAccount(response.account);
          }
          setLoginError(null);
          setAccessDenied(false);
        } catch (authError) {
          if (isInteractionInProgressError(authError)) {
            return null;
          }
          console.error("Interactive login failed:", authError);
          setLoginError(authError?.errorMessage || authError?.message || String(authError));
        }
      }
    }
  };

  // ✅ Set active account on load
  const acquireTokenSilentOnly = async (scopes) => {
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({ scopes, account, ...(authority ? { authority } : {}) });
      return response.accessToken;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      sessionStorage.removeItem("loginAttempted");
      setLoginError(null);
      setAccessDenied(false);
      if (lockAfterFirstLogin) {
        setLoginLock();
        setLoginLocked(true);
      }
    }
  }, [accounts, instance, lockAfterFirstLogin]);

  useEffect(() => {
    if (!lockAfterFirstLogin) {
      setLoginLocked(false);
      return;
    }
    setLoginLocked(hasLoginLock());
  }, [lockAfterFirstLogin]);

  useEffect(() => {
    if (!lockAfterFirstLogin) return;
    const handleStorage = (event) => {
      if (event?.key && event.key !== SIGN_IN_LOCK_KEY) return;
      setLoginLocked(hasLoginLock());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [lockAfterFirstLogin]);

  useEffect(() => {
    if (!showProfilePicture) {
      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
      setProfilePhotoUrl(null);
      photoAccountRef.current = null;
      return;
    }

    if (!isAuthenticated || accounts.length === 0) {
      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
      setProfilePhotoUrl(null);
      photoAccountRef.current = null;
      return;
    }

    const account = instance.getActiveAccount() || accounts[0];
    const accountId = account?.homeAccountId || account?.localAccountId || account?.username;

    if (!accountId || fetchingPhotoRef.current) {
      return;
    }

    if (photoAccountRef.current === accountId && profilePhotoUrl) {
      return;
    }

    fetchingPhotoRef.current = true;
    (async () => {
      try {
        const token = await acquireTokenSilentOnly(graphRequest.scopes);
        if (!token) return;

        const response = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const blob = await response.blob();
        if (!blob || !blob.size) {
          return;
        }

        const url = URL.createObjectURL(blob);
        if (photoUrlRef.current) {
          URL.revokeObjectURL(photoUrlRef.current);
        }
        photoUrlRef.current = url;
        photoAccountRef.current = accountId;
        setProfilePhotoUrl(url);
      } catch (error) {
        console.warn("Failed to fetch profile photo:", error);
      }
    })().finally(() => {
      fetchingPhotoRef.current = false;
    });
  }, [showProfilePicture, isAuthenticated, accounts, instance, profilePhotoUrl]);

  useEffect(() => {
    return () => {
      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      const errorMessage = event?.error?.errorMessage || event?.error?.message;
      if (!errorMessage) return;

      const errorCode = event?.error?.errorCode || "";
      if (errorCode === "interaction_in_progress") {
        return;
      }

      setLoginError(errorMessage);
      if (errorMessage.includes("AADSTS50105")) {
        setAccessDenied(true);
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  // ✅ Initial login or token fetch
  useEffect(() => {
    const initialize = async () => {
      const authError = getAuthErrorFromUrl();
      if (authError) {
        setLoginError(authError);
      }

      const returnedFromRedirect = hasAuthResponseInUrl();
      if (returnedFromRedirect && inProgress !== InteractionStatus.None) {
        // Let MSAL finish processing the redirect first.
        return;
      }

      if (returnedFromRedirect) {
        clearAuthResponseFromHash();
        sessionStorage.removeItem("loginAttempted");
        // Avoid triggering a new loginRedirect before `isAuthenticated` reflects the redirect result.
        return;
      }

      if (!isAuthenticated && inProgress === InteractionStatus.None && !SessionStorageService.getUserId()) {
        if (lockAfterFirstLogin && loginLocked) {
          return;
        }
        if (isMsalInteractionInProgress()) {
          return;
        }
        const loginAttempted = sessionStorage.getItem("loginAttempted") === "true";
        if (loginAttempted) {
          // Avoid infinite login redirects when the IdP returns an error (AADSTS*).
          setLoginError((prev) => prev || "Sign-in failed. Check Entra app Redirect URI and user assignment.");
          return;
        }
        sessionStorage.setItem("loginAttempted", "true");
        beginInteractiveLogin(authRequest).catch(error => {
          if (isInteractionInProgressError(error)) {
            return;
          }
          console.error("Login error:", error);
          setLoginError(error?.errorMessage || error?.message || String(error));
          if (
            error?.errorCode === "AADSTS50105" ||
            error?.errorCode === "50105" ||
            (error?.message && error.message.includes("AADSTS50105"))
          ) {
            setAccessDenied(true);
          }
        });
        return;
      }

      if (!SessionStorageService.getUserId() && isAuthenticated && inProgress === InteractionStatus.None) {
        await acquireToken(authRequest.scopes);
        const graphToken = await acquireToken(graphRequest.scopes);
        sessionStorage.removeItem("loginAttempted");
      }
      callback();
    };

    initialize();
  }, [instance, inProgress, isAuthenticated, accounts, authRequest, lockAfterFirstLogin, loginLocked]);


  // ✅ Periodic token refresh
  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated && inProgress === InteractionStatus.None) {
        // Refresh silently only; avoid unexpected popups/redirects.
        acquireTokenSilentOnly(authRequest.scopes);
        acquireTokenSilentOnly(graphRequest.scopes);
      }
    }, 10 * 60 * 1000); // Every 10 minutes

    return () => clearInterval(interval);
  }, [instance, inProgress, isAuthenticated, authRequest]);


  return (
    <div>
      {loginLocked && !isAuthenticated && (
        <div className="white" style={{ fontSize: "0.875rem" }}>
          {isEng
            ? "Sign-in is disabled after the first login. Clear browser storage to sign in again."
            : "Connexion desactivee apres la premiere connexion. Effacez le stockage du navigateur pour vous reconnecter."}
        </div>
      )}
      {loginError && !isAuthenticated && !loginLocked && (
        <details className="white" style={{ fontSize: "0.875rem" }}>
          <summary>{accessDenied ? "Access denied." : "Sign-in error."}</summary>
          <div style={{ maxWidth: 520, wordBreak: "break-word" }}>{loginError}</div>
          <button
            type="button"
            onClick={async () => {
              sessionStorage.removeItem("loginAttempted");
              setLoginError(null);
              setAccessDenied(false);
              if (isMsalInteractionInProgress()) {
                return;
              }
              try {
                await beginInteractiveLogin(authRequest);
              } catch (error) {
                if (isInteractionInProgressError(error)) {
                  return;
                }
                setLoginError(error?.errorMessage || error?.message || String(error));
              }
            }}
            style={{ marginTop: 6 }}
          >
            Try sign-in again
          </button>
        </details>
      )}
      <AuthenticatedTemplate>
        <ProfileContent
          isEng={isEng}
          displayName={accounts?.[0]?.name || accounts?.[0]?.username || ""}
          photoUrl={profilePhotoUrl}
          showProfilePicture={showProfilePicture}
        />
      </AuthenticatedTemplate>
    </div>
  );
}

// ✅ Utility to get user info externally
export const getAzureUserInfo = () => {
  try {
    const account = msalInstance.getAllAccounts()[0];
    return account || null;
  } catch (error) {
    console.error("Error getting Azure user info:", error);
    return null;
  }
};
 
